import { useEffect, useRef, useState } from "react";
import { goBack, pushScreen } from "../backstack";
import {
  AI_MODEL_LABELS,
  formatAiMeta,
  type AiMeta,
  type AiModel,
  type InventorySuggestion,
  api,
  INVENTORY_LOCATIONS,
  type Driver,
  type InventoryInput,
  type InventoryItem,
  isInventoryLocation,
  type InventoryLocation,
} from "../api";
import BackHeader from "../components/BackHeader";
import { prepareImage } from "../scheduler/photos";
import TagInput from "./TagInput";

// Quick prompt snippets for the Help AI dialog; a tap appends one to the prompt.
const AI_PROMPT_EXAMPLES = [
  "Max 10 words.",
  "Only the item name.",
  "Ignore the box, describe what is inside.",
  "Read the brand and model from the label.",
  "Count how many there are.",
  "Note any damage or wear.",
  "Include size or dimensions if visible.",
  "Only the item on the left.",
  "Description in Norwegian.",
  "Tags in Norwegian.",
];

const FIELD =
  "w-full rounded-[14px] bg-white px-3.5 font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20";
const LABEL = "px-1 text-[12.5px] font-semibold text-[#3d2930]";

// What the model sees. 896 px is still plenty to read a label (the models resize to
// roughly this anyway); WebP at 0.72 makes it ~30 KB instead of ~90 KB as JPEG, which
// is what matters on a weak connection in the storehouse.
const AI_SIDE = 896;
const AI_QUALITY = 0.72;
const AI_TYPE = "image/webp";
const THUMB_SIDE = 480;
const MAX_AI_PHOTOS = 3;

type NewPhoto = { key: number; full: Blob; thumb: Blob; ai: Blob; url: string };
type AiState =
  | { status: "idle" }
  | { status: "running" }
  | { status: "done" }
  | { status: "unavailable" }
  | { status: "error"; message: string };

const LAST_LOCATION_KEY = "inventoryLastLocation";

/** The place used last time — cataloguing usually happens one room at a time. */
export function lastLocation(): InventoryLocation {
  const saved = localStorage.getItem(LAST_LOCATION_KEY);
  return isInventoryLocation(saved) ? saved : "warehouse";
}

/**
 * New item: photos first, then an AI suggestion fills the fields (anything the
 * user already typed is left alone). Edit: same fields for an existing item;
 * its photos are managed on the detail screen.
 */
export default function ItemForm({
  driver,
  initial,
  defaultLocation,
  firstPhotos,
  onSaved,
  onCancel,
}: {
  driver: Driver;
  initial?: InventoryItem;
  defaultLocation?: InventoryLocation;
  firstPhotos?: File[];
  onSaved: (item: InventoryItem, note?: string) => void;
  onCancel: () => void;
}) {
  const editing = !!initial;
  const [fields, setFields] = useState<InventoryInput>(
    initial
      ? {
          description: initial.description,
          itemType: initial.itemType,
          brand: initial.brand,
          model: initial.model,
          tags: initial.tags,
          location: initial.location,
        }
      : { description: "", itemType: "", brand: "", model: "", tags: [], location: defaultLocation ?? lastLocation() },
  );
  const [photos, setPhotos] = useState<NewPhoto[]>([]);
  const [preparing, setPreparing] = useState(0);
  const [ai, setAi] = useState<AiState>({ status: "idle" });
  // "Help AI" dialog: which model to use and extra instructions for the retry.
  const [aiModel, setAiModel] = useState<AiModel>("deepseek");
  const [aiHint, setAiHint] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false); // the "?" next to "Tell AI what you want"
  const [examplesOpen, setExamplesOpen] = useState(false); // quick-pick prompt snippets
  const addExample = (text: string) => {
    setAiHint((h) => (h.trim() ? `${h.trim().replace(/[.。]?$/, ".")} ${text}` : text));
    setExamplesOpen(false);
  };
  const [aiMeta, setAiMeta] = useState<AiMeta | null>(null); // last successful AI call
  // A trial run inside the Help AI dialog: shown there, applied only on Save.
  const [trial, setTrial] = useState<
    { status: "idle" } | { status: "running" } | { status: "done"; result: InventorySuggestion } | { status: "error"; message: string }
  >({ status: "idle" });
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const touched = useRef(new Set<keyof InventoryInput>());
  const nextKey = useRef(1);
  const autoAnalyzed = useRef(false);

  const setField = <K extends keyof InventoryInput>(key: K, value: InventoryInput[K]) => {
    touched.current.add(key);
    setFields((f) => ({ ...f, [key]: value }));
  };

  const analyze = async (images: Blob[], options: { model?: AiModel; hint?: string } = {}) => {
    if (!images.length) return;
    setAi({ status: "running" });
    try {
      const s = await api.analyzeInventoryPhotos(images.slice(0, MAX_AI_PHOTOS), options);
      // Never overwrite what the user typed themselves.
      setFields((f) => ({
        ...f,
        description: touched.current.has("description") ? f.description : s.description || f.description,
        itemType: touched.current.has("itemType") ? f.itemType : s.itemType || f.itemType,
        brand: touched.current.has("brand") ? f.brand : s.brand || f.brand,
        model: touched.current.has("model") ? f.model : s.model || f.model,
        tags: touched.current.has("tags") ? f.tags : s.tags.length ? s.tags : f.tags,
      }));
      setAiMeta(s.meta ?? null);
      setAi({ status: "done" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Suggestion failed";
      setAi(message.includes("not set up") ? { status: "unavailable" } : { status: "error", message });
    }
  };

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    setPreparing((n) => n + files.length);
    const added: NewPhoto[] = [];
    for (const file of files) {
      try {
        const full = await prepareImage(file);
        const [thumb, aiSized] = await Promise.all([
          prepareImage(full, THUMB_SIDE, 0.72),
          prepareImage(full, AI_SIDE, AI_QUALITY, AI_TYPE),
        ]);
        added.push({ key: nextKey.current++, full, thumb, ai: aiSized, url: URL.createObjectURL(thumb) });
      } finally {
        setPreparing((n) => n - 1);
      }
    }
    setPhotos((list) => [...list, ...added]);
    // The first photo(s) kick off the suggestion automatically; later ones are manual.
    if (!autoAnalyzed.current && added.length) {
      autoAnalyzed.current = true;
      analyze(added.map((p) => p.ai));
    }
  };

  useEffect(() => {
    if (firstPhotos?.length) addFiles(firstPhotos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => photos.forEach((p) => URL.revokeObjectURL(p.url)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const removePhoto = (key: number) =>
    setPhotos((list) => {
      list.filter((p) => p.key === key).forEach((p) => URL.revokeObjectURL(p.url));
      return list.filter((p) => p.key !== key);
    });

  // Edit mode has no local photos — look at the ones already stored.
  const loadExistingBlobs = () =>
    Promise.all(
      (initial?.photoIds ?? []).slice(0, MAX_AI_PHOTOS).map(async (id) => {
        const res = await fetch(api.inventoryPhotoUrl(id));
        return prepareImage(await res.blob(), AI_SIDE, AI_QUALITY, AI_TYPE);
      }),
    );
  const analyzeExisting = async (options: { model?: AiModel; hint?: string } = {}) => {
    if (!initial?.photoIds.length) return;
    setAi({ status: "running" });
    try {
      await analyze(await loadExistingBlobs(), options);
    } catch {
      setAi({ status: "error", message: "Couldn't load the photos" });
    }
  };

  // "Help AI" dialog: Retry runs the chosen model + prompt and shows the answer in the
  // dialog; the form is untouched until Save, so you can try until you're happy.
  const runTrial = async () => {
    setTrial({ status: "running" });
    try {
      const images = editing ? await loadExistingBlobs() : photos.map((p) => p.ai);
      if (!images.length) throw new Error("No photos to analyse");
      const result = await api.analyzeInventoryPhotos(images.slice(0, MAX_AI_PHOTOS), { model: aiModel, hint: aiHint });
      setTrial({ status: "done", result });
    } catch (err) {
      setTrial({ status: "error", message: err instanceof Error ? err.message : "Suggestion failed" });
    }
  };
  const saveTrial = () => {
    if (trial.status !== "done") return;
    const s = trial.result;
    touched.current.clear(); // an explicit Save replaces every field
    setFields((f) => ({ ...f, description: s.description, itemType: s.itemType, brand: s.brand, model: s.model, tags: s.tags }));
    setAiMeta(s.meta ?? null);
    setAi({ status: "done" });
    goBack();
  };
  const openHelp = () => {
    setTrial({ status: "idle" });
    setHelpOpen(true);
    pushScreen(() => setHelpOpen(false));
  };

  const save = async () => {
    if (saving) return;
    setError(null);
    try {
      if (initial) {
        setSaving("Saving…");
        onSaved(await api.updateInventoryItem(initial.id, fields));
        return;
      }
      setSaving("Saving…");
      localStorage.setItem(LAST_LOCATION_KEY, fields.location);
      const item = await api.createInventoryItem(fields, driver.id);
      const photoIds: number[] = [];
      let failed = 0;
      for (const [i, photo] of photos.entries()) {
        setSaving(`Uploading photo ${i + 1} of ${photos.length}…`);
        try {
          photoIds.push((await api.uploadInventoryPhoto(item.id, photo.full, photo.thumb)).id);
        } catch {
          failed++;
        }
      }
      onSaved(
        { ...item, photoIds },
        failed ? `Saved, but ${failed} photo${failed === 1 ? "" : "s"} failed to upload — add again from the item.` : undefined,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save");
      setSaving(null);
    }
  };

  const busy = saving !== null;
  const canSave = !busy && preparing === 0 && (editing || photos.length > 0);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <BackHeader title={editing ? "Edit item" : "New item"} onBack={onCancel} />

      {!editing && (
        <section className="flex flex-col gap-2">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((p) => (
              <div key={p.key} className="flex shrink-0 flex-col items-center gap-0.5">
                <div className="relative size-28 overflow-hidden rounded-[14px] bg-white border border-line-soft shadow-card">
                  <img src={p.url} alt="" className="size-full object-cover" />
                  <button
                    onClick={() => removePhoto(p.key)}
                    disabled={busy}
                    aria-label="Remove photo"
                    className="absolute right-1 top-1 flex size-7 items-center justify-center rounded-full bg-ink/70 text-sm font-bold text-white"
                  >
                    ✕
                  </button>
                </div>
                {/* Size of the copy sent to the AI — the part that is slow on a weak connection. */}
                <span className="text-[10px] font-semibold leading-none text-ink/50" title={`Sent to AI: ${Math.round(p.ai.size / 1024)} kb · stored: ${Math.round(p.full.size / 1024)} kb`}>
                  {Math.round(p.ai.size / 1024)} kb
                </span>
              </div>
            ))}
            {preparing > 0 && (
              <div className="flex size-28 shrink-0 items-center justify-center rounded-[14px] bg-white border border-line">
                <span className="size-6 animate-spin rounded-full border-[3px] border-hot border-t-transparent" />
              </div>
            )}
            <label className="flex size-28 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-[14px] bg-blush text-sm font-bold text-rose-ink active:scale-95">
              <span className="text-2xl" aria-hidden>
                📷
              </span>
              {photos.length ? "Another" : "Take photo"}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  addFiles([...(e.target.files ?? [])]);
                  e.target.value = "";
                }}
              />
            </label>
            <label className="flex size-28 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-[14px] bg-white text-sm font-bold active:scale-95 border border-line-soft shadow-card">
              <span className="text-2xl" aria-hidden>
                🖼️
              </span>
              Library
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  addFiles([...(e.target.files ?? [])]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
        </section>
      )}

      {/* Same single row as the list filter; the selected place is the dark one. */}
      <div role="radiogroup" aria-label="Where is it?" className="flex gap-1">
        {INVENTORY_LOCATIONS.map((loc) => {
          const on = fields.location === loc.value;
          return (
            <button
              key={loc.value}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={loc.store ? `Store ${loc.label}` : loc.label}
              onClick={() => setField("location", loc.value)}
              disabled={busy}
              className={`h-11 rounded-[14px] text-xs font-bold shadow-sm transition-colors active:scale-95 ${
                loc.store ? "min-w-0 flex-1 px-0" : "shrink-0 px-2.5"
              } ${on ? "bg-ink text-white" : "bg-white text-ink/60"}`}
            >
              {loc.label}
            </button>
          );
        })}
      </div>

      {aiMeta && ai.status !== "running" && (
        <p className="-mb-3 px-1 text-[11px] font-semibold leading-none text-ink/50">{formatAiMeta(aiMeta)}</p>
      )}
      <div className="flex min-h-9 items-center justify-between gap-3 px-1">
        <p className="text-sm font-semibold text-ink/60">
          {ai.status === "running" && (
            <span className="flex items-center gap-2">
              <span className="size-4 animate-spin rounded-full border-2 border-hot border-t-transparent" />
              Looking at the photo{photos.length > 1 ? "s" : ""}…
            </span>
          )}
          {ai.status === "done" && "✨ Suggested from the photo — change the suggestion or click on Help AI."}
          {ai.status === "error" && <span className="text-hot">Suggestion failed: {ai.message}</span>}
          {ai.status === "unavailable" && "AI suggestions aren't set up yet — just type a description."}
          {ai.status === "idle" && !editing && photos.length === 0 && "Take a photo and a description is suggested for you."}
        </p>
        {ai.status !== "running" && ai.status !== "unavailable" && (editing ? !!initial?.photoIds.length : photos.length > 0) && (
          <button
            onClick={ai.status === "idle" ? () => (editing ? analyzeExisting() : analyze(photos.map((p) => p.ai))) : openHelp}
            disabled={busy}
            className="shrink-0 rounded-full bg-blush px-3 py-1.5 text-xs font-bold text-rose-ink active:scale-95"
          >
            ✨ {ai.status === "idle" ? "Suggest" : "Help AI"}
          </button>
        )}
      </div>

      <label className="flex flex-col gap-1.5">
        <span className={LABEL}>Description</span>
        <textarea
          value={fields.description}
          onChange={(e) => setField("description", e.target.value.slice(0, 500))}
          rows={3}
          placeholder="What is it? (can be left empty)"
          className={`${FIELD} resize-none py-3`}
        />
      </label>

      <div className="grid grid-cols-3 gap-2">
        {(
          [
            ["itemType", "Type", "e.g. table"],
            ["brand", "Brand", ""],
            ["model", "Model", ""],
          ] as const
        ).map(([key, label, placeholder]) => (
          <label key={key} className="flex min-w-0 flex-col gap-1">
            <span className="px-1 text-[11px] font-semibold text-ink-soft">{label}</span>
            <input
              type="text"
              value={fields[key]}
              onChange={(e) => setField(key, e.target.value.slice(0, 80))}
              placeholder={placeholder}
              className="h-10 w-full rounded-xl bg-white px-2.5 !text-sm font-semibold outline-none placeholder:text-ink/40 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20"
            />
          </label>
        ))}
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={LABEL}>Tags</span>
        <TagInput tags={fields.tags} onChange={(tags) => setField("tags", tags)} />
      </div>

      {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}

      <button
        onClick={save}
        disabled={!canSave}
        className="mt-auto h-16 w-full rounded-[14px] bg-rose-grad text-lg font-bold text-white transition-transform active:scale-[0.98] disabled:opacity-60 shadow-button"
      >
        {saving ?? (editing ? "Save changes" : photos.length ? "Save item" : "Take a photo first")}
      </button>

      {tipsOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 px-6" onClick={() => setTipsOpen(false)}>
          <div
            className="pop-in flex max-h-[90dvh] w-full max-w-sm flex-col gap-2.5 overflow-y-auto rounded-[20px] bg-white p-4 shadow-pop"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[19px] font-bold tracking-[-0.02em]">Tell AI what you want</h2>
            <p className="text-xs font-semibold text-ink/70">
              The AI looks at the photos and fills in description, type, brand, model and tags. Type a short instruction
              to steer what it writes. Plain language is fine.
            </p>
            <ul className="flex flex-col gap-1 text-xs font-semibold text-ink/80">
              {[
                ["Shorter", "“Max 10 words.” · “Only the item name.”"],
                ["Focus", "“Ignore the box, describe what is inside.” · “Only the item on the left.”"],
                ["Read the label", "“Write the brand, model and serial number from the label.”"],
                ["Count", "“How many are there? Put the number first.”"],
                ["Condition", "“Note any damage or wear.”"],
                ["Language", "“Description in Norwegian.” · “Tags in Norwegian.”"],
                ["Fix a mistake", "“It is a pedestal fan, not a lamp.”"],
              ].map(([title, example]) => (
                <li key={title} className="rounded-xl bg-blush-soft px-3 py-1.5">
                  <span className="text-hot">{title}:</span> {example}
                </li>
              ))}
            </ul>
            <p className="text-xs font-semibold text-ink/50">
              Then tap Try. Nothing changes in the form until you press Save, so try as many times as you like.
            </p>
            <button type="button" onClick={() => setTipsOpen(false)} className="h-11 rounded-full bg-rose-grad font-bold text-white shadow-button">
              Got it
            </button>
          </div>
        </div>
      )}
      {helpOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:px-6" onClick={goBack}>
          <div
            className="pop-in flex max-h-[92dvh] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-t-[22px] bg-white p-5 shadow-pop sm:rounded-[20px]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[19px] font-bold tracking-[-0.02em]">Help AI</h2>
            {aiMeta && <p className="-mt-2 text-xs font-semibold text-ink/50">Last run — {formatAiMeta(aiMeta)}</p>}

            {/* Model chips, like the filter tabs: one row, the chosen one dark. */}
            <div role="radiogroup" aria-label="Model" className="flex gap-2">
              {(Object.keys(AI_MODEL_LABELS) as AiModel[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={aiModel === key}
                  title={AI_MODEL_LABELS[key].detail}
                  onClick={() => setAiModel(key)}
                  className={`flex-1 rounded-[14px] px-3 py-1.5 text-xs font-bold shadow-sm transition-colors ${
                    aiModel === key ? "bg-ink text-white" : "bg-blush-soft text-ink/60"
                  }`}
                >
                  {AI_MODEL_LABELS[key].name}
                </button>
              ))}
            </div>

            <label className="flex flex-col gap-1.5">
              <span className="flex items-center gap-2 px-1">
                <span className={LABEL.replace("px-1 ", "")}>Tell AI what you want</span>
                <button
                  type="button"
                  onClick={() => setTipsOpen(true)}
                  aria-label="What can I type here?"
                  className="flex size-5 items-center justify-center rounded-full bg-lavender text-[11px] font-bold text-ink"
                >
                  ?
                </button>
                <button
                  type="button"
                  onClick={() => setExamplesOpen((o) => !o)}
                  aria-expanded={examplesOpen}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${examplesOpen ? "bg-ink text-white" : "bg-lavender text-ink"}`}
                >
                  Examples
                </button>
              </span>
              {examplesOpen && (
                <div className="flex flex-wrap gap-1.5 px-1 pb-1">
                  {AI_PROMPT_EXAMPLES.map((ex) => (
                    <button
                      key={ex}
                      type="button"
                      onClick={() => addExample(ex)}
                      className="rounded-full bg-blush-soft px-3 py-1.5 text-xs font-semibold text-ink/80 active:scale-95"
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              )}
              <textarea
                value={aiHint}
                onChange={(e) => setAiHint(e.target.value.slice(0, 300))}
                rows={3}
                placeholder="Make text shorter, only 30 words, don't look at the box…"
                className={`${FIELD} resize-none py-3`}
              />
              <span className="px-1 text-xs font-semibold text-ink/40">Added to the AI's instructions.</span>
            </label>

            <button
              type="button"
              onClick={runTrial}
              disabled={trial.status === "running"}
              className="mx-auto flex h-12 items-center gap-2 rounded-[14px] bg-blush px-8 font-bold text-rose-ink transition-transform active:scale-95 disabled:opacity-60"
            >
              {trial.status === "running" ? (
                <>
                  <span className="size-4 animate-spin rounded-full border-2 border-hot border-t-transparent" /> Asking…
                </>
              ) : (
                "✨ Try"
              )}
            </button>

            {trial.status === "error" && <p className="text-center text-sm font-semibold text-danger">{trial.message}</p>}
            {trial.status === "done" && (
              <div className="flex flex-col gap-1.5 rounded-[14px] bg-blush-soft p-4">
                <p className="font-semibold">{trial.result.description || <span className="text-ink/40">No description</span>}</p>
                {(trial.result.itemType || trial.result.brand || trial.result.model) && (
                  <p className="text-xs font-semibold text-ink/60">
                    {[trial.result.itemType, [trial.result.brand, trial.result.model].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                  </p>
                )}
                {trial.result.tags.length > 0 && <p className="text-xs font-semibold text-ink/50">{trial.result.tags.join(", ")}</p>}
                {trial.result.meta && <p className="pt-1 text-[11px] font-semibold text-ink/50">{formatAiMeta(trial.result.meta)}</p>}
              </div>
            )}

            <div className="flex gap-2">
              <button type="button" onClick={goBack} className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveTrial}
                disabled={trial.status !== "done"}
                className="h-12 flex-1 rounded-[14px] bg-rose-grad font-bold text-white disabled:opacity-40 shadow-button"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
